import React, { useEffect, useRef, useState } from 'react';

import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';

// A server error is text when we wrote it; anything else (a framework error body) is not fit to show
export const errorText = (data, fallback = 'Something went wrong') => (typeof data === 'string' && data ? data : fallback);

// The two things both Settings and the sign-in page show when someone sets up two-step sign-in:
// the QR code to scan (with a typed secret for those who cannot scan), and the recovery codes.

/** Shows the QR code from `start()` and asks for the app's first code; `confirm(code)` returns an error text or null when it worked. */
export function TwoFactorSetupPanel({ start, confirm, busyLabel = 'Turn on', extra = null }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const pending = useRef(null);

  useEffect(() => {
    // One start only (each makes a new secret, and the one on screen must be the one the server holds); a second run of
    // this effect, as React's development mode does, waits for the same answer
    if (!pending.current) pending.current = start();
    let cancelled = false;
    pending.current.then((result) => {
      if (cancelled) return;
      if (result.error) setError(result.error); else setSetup(result);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    const problem = await confirm(code);
    if (problem) {
      setError(problem);
      setCode('');
    }
    setBusy(false);
  };

  if (!setup) {
    return error ? <Alert variant="danger">{error}</Alert> : <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>;
  }
  return (
    <Form onSubmit={submit}>
      <ol className="ps-3 mb-3 small">
        <li className="mb-2">Install an authenticator app on your phone, such as Google Authenticator, Microsoft Authenticator or Authy.</li>
        <li className="mb-2">In the app, add an account and scan this picture.</li>
      </ol>
      <div className="d-flex justify-content-center mb-2" data-testid="two-factor-qr">
        <div className="p-2 border rounded" style={{ backgroundColor: '#fff' }}><QRCodeSVG value={setup.otpauthUri} size={168} /></div>
      </div>
      <p className="small text-muted text-center mb-3">
        Can't scan? Type this key into the app instead: <code className="user-select-all" data-testid="two-factor-secret">{setup.secret.match(/.{1,4}/g).join(' ')}</code>
      </p>
      <Form.Group className="mb-3">
        <Form.Label>3. Type the 6-digit code the app shows</Form.Label>
        <Form.Control
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={7}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="123456"
          data-testid="two-factor-setup-code"
          required
          autoFocus
        />
      </Form.Group>
      {error && <Alert variant="danger" className="py-2">{error}</Alert>}
      {extra}
      <Button type="submit" variant="primary" disabled={busy || code.replace(/\s/g, '').length < 6} data-testid="two-factor-setup-confirm">
        {busy ? 'Checking...' : busyLabel}
      </Button>
    </Form>
  );
}

/** The recovery codes, shown once: copy, download or print them, then confirm they are saved. */
export function RecoveryCodesPanel({ codes, onDone, doneLabel = 'I have saved these codes' }) {
  const [saved, setSaved] = useState(false);
  const text = codes.join('\n');

  const copy = async () => {
    try { await navigator.clipboard.writeText(text); } catch (e) { /* the codes are on screen */ }
  };
  const download = () => {
    const blob = new Blob([`iBigDo recovery codes\nEach works once, if you lose your phone.\n\n${text}\n`], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'ibigdo-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const print = () => {
    const w = window.open('', '_blank', 'width=420,height=520');
    if (!w) return;
    w.document.write(`<pre style="font-size:18px;line-height:1.8">iBigDo recovery codes\nEach works once.\n\n${text}</pre>`);
    w.document.close();
    w.print();
  };

  return (
    <div>
      <Alert variant="warning" className="py-2 small">
        Keep these somewhere safe. If you lose your phone, each code lets you sign in once. They are shown only now.
      </Alert>
      <div className="border rounded p-3 mb-3" style={{ columns: 2, fontFamily: 'monospace', fontSize: '1.05rem', backgroundColor: 'var(--bs-tertiary-bg)', color: 'var(--bs-body-color)' }} data-testid="recovery-codes">
        {codes.map((c) => <div key={c}>{c}</div>)}
      </div>
      <div className="d-flex gap-2 mb-3 flex-wrap">
        <Button size="sm" variant="outline-secondary" onClick={copy}><i className="bi bi-clipboard me-1" />Copy</Button>
        <Button size="sm" variant="outline-secondary" onClick={download}><i className="bi bi-download me-1" />Download</Button>
        <Button size="sm" variant="outline-secondary" onClick={print}><i className="bi bi-printer me-1" />Print</Button>
      </div>
      <Form.Check
        type="checkbox"
        id="recovery-saved"
        className="mb-3"
        label="I have saved these codes"
        checked={saved}
        onChange={(e) => setSaved(e.target.checked)}
      />
      <Button variant="primary" disabled={!saved} onClick={onDone} data-testid="recovery-done">{doneLabel}</Button>
    </div>
  );
}
